// Copyright 2020 Sorama B.V.

import { autoinject, BindingEngine, computedFrom, Disposable, observable } from "aurelia-framework";
import { MeasurementProcessorTypes } from "datatypes/MeasurementProcessorTypes";
import { Point2D } from "datatypes/SoramaLocalApi";
import { GenericMeasurement } from "datatypes/SoramaWebApi";
import { IModal } from "libraries/WorkflowFramework/Interfaces/IModal";
import { IWorkflowModuleModalInstance } from "libraries/WorkflowFramework/Interfaces/IWorkflowModule";
import { MeasurementRepository } from "repositories/MeasurementRepository";
import { SelectableRange } from "services/FrequencySelection/FrequencySelectionService";

class SignalSelection {
    constructor(set: TimeDataSet, supported: boolean) {
        this.measurement = set.measurement;
        this.isMeasurementSupported = supported;
    }

    public measurement: GenericMeasurement;
    public isMeasurementSupported: boolean;
}

@autoinject
export class TimeSelectionModal implements IModal<TimeSelectionModalState, void> {
    private state: TimeSelectionModalState;
    private signals: SignalSelection[];
    private modalInstance: IWorkflowModuleModalInstance<TimeSelectionModalState, void>;
    protected isCreatingMeasurements = true;

    @observable
    public name: string;
    protected selectedSignal: SignalSelection;
    private rangeDisposables: Disposable[];

    @computedFrom("selectedSignal", "state")
    protected get canCreateBeamfromProbe(): boolean {
        return (
            this.isValidStateAndSignal() &&
            this.selectedSignal.measurement.isComputedMeasurement &&
            !this.selectedSignal.measurement.computedMeasurementProperties.Properties.TimeRange
        );
    }

    @computedFrom("selectedSignal", "state")
    protected get isTheOriginalTimeSelection(): boolean {
        return (
            this.isValidStateAndSignal() &&
            this.selectedSignal.measurement.isComputedMeasurement &&
            this.selectedSignal.measurement.computedMeasurementProperties.Properties.TimeRange
        );
    }

    @computedFrom("selectedSignal", "state")
    protected get isStrictTimeRangeLimit(): boolean {
        return (
            this.isValidStateAndSignal() &&
            this.selectedSignal.measurement.duration * 1000 > Number(this.state.selectedTimeRange.from.toFixed(0)) &&
            this.selectedSignal.measurement.duration * 1000 < Number(this.state.selectedTimeRange.to.toFixed(0))
        );
    }

    @computedFrom("selectedSignal", "state")
    protected get isTimeRangeAvailable(): boolean {
        return (
            this.isValidStateAndSignal() &&
            this.selectedSignal.measurement.duration * 1000 < Number(this.state.selectedTimeRange.from.toFixed(0)) &&
            this.selectedSignal.measurement.duration * 1000 < Number(this.state.selectedTimeRange.to.toFixed(0))
        );
    }

    @computedFrom("selectedSignal", "state")
    protected get isTimeRangeLarge(): boolean {
        return (
            this.isValidStateAndSignal() &&
            this.state.selectedTimeRange.to - this.state.selectedTimeRange.from < 10 &&
            this.state.selectedTimeRange.from > 1
        );
    }

    constructor(
        private readonly measurementRepository: MeasurementRepository,
        private readonly bindingEngine: BindingEngine
    ) {}

    public activateModal(
        state: TimeSelectionModalState,
        modalInstance: IWorkflowModuleModalInstance<TimeSelectionModalState, void>
    ): void {
        this.modalInstance = modalInstance;
        this.updateState(state);
        this.isCreatingMeasurements = false;
    }

    public updateState(state: TimeSelectionModalState): void {
        if (!state || !state.selectedTimeRange) return; // Early return if state or time range is invalid

        const newName = this.formatTimeRange(state.selectedTimeRange);
        const oldName = this.state?.selectedTimeRange ? this.formatTimeRange(this.state.selectedTimeRange) : undefined;

        // Dispose of previous observers if any
        this.disposeRangeDisposables();

        // Update name if it's unchanged or uninitialized
        if (oldName === this.name || !this.name) {
            this.name = newName;

            // Setup new observers if a time range is selected
            if (state.selectedTimeRange) {
                this.rangeDisposables = [
                    this.bindingEngine
                        .propertyObserver(state.selectedTimeRange, "from")
                        .subscribe(() => this.updateMeasurementName(state)),
                    this.bindingEngine
                        .propertyObserver(state.selectedTimeRange, "to")
                        .subscribe(() => this.updateMeasurementName(state)),
                ];
            }
        }

        // Update state and signals
        this.state = state;
        this.signals = state.currentSignals.map(
            (x) => new SignalSelection(x, this.isMeasurementSupported(x.measurement))
        );
    }

    private updateMeasurementName(state: TimeSelectionModalState): void {
        const newName =
            state && state.selectedTimeRange
                ? state.selectedTimeRange.from.toFixed(0) + " ms - " + state.selectedTimeRange.to.toFixed(0) + " ms"
                : undefined;

        if (newName) {
            this.name = newName;
        }

        // Check if time range is supported
        for (const signal of this.signals) {
            signal.isMeasurementSupported = this.isMeasurementSupported(signal.measurement);
        }
    }

    public close(): void {
        return;
    }

    public createMeasurements(): void {
        this.isCreatingMeasurements = true;

        const promises: Promise<GenericMeasurement>[] = [];

        for (const measurement of this.signals.filter((x) => x == this.selectedSignal)) {
            if (this.isMeasurementSupported(measurement.measurement)) {
                let offset = 0;
                let parentMeasurement = measurement.measurement;
                let maxTime = parentMeasurement.duration;

                if (
                    measurement.measurement.isComputedMeasurement &&
                    measurement.measurement.computedMeasurementProperties.Properties.TimeRange
                ) {
                    parentMeasurement = measurement.measurement.computedMeasurementSource;
                    offset = measurement.measurement.computedMeasurementProperties.Properties.TimeRange.From;
                    maxTime += offset;
                }

                promises.push(
                    this.measurementRepository.createTimeRangeGenericMeasurement(this.name, parentMeasurement.id, {
                        timeRange: {
                            from: Math.max(offset, offset + this.state.selectedTimeRange.from / 1000),
                            to: Math.min(maxTime, offset + this.state.selectedTimeRange.to / 1000),
                        },
                    })
                );
            }
        }

        Promise.all(promises)
            .then(() => this.modalInstance.close())
            .catch((error) => console.error("Error creating measurements", error));
    }

    private formatTimeRange(range: SelectableRange): string {
        return `${range.from.toFixed(0)} ms - ${range.to.toFixed(0)} ms`;
    }

    private disposeRangeDisposables(): void {
        if (this.rangeDisposables) {
            this.rangeDisposables.forEach((x) => x.dispose());
            this.rangeDisposables = undefined;
        }
    }

    private isMeasurementSupported(measurement: GenericMeasurement): boolean {
        if (
            measurement.isComputedMeasurement &&
            measurement.computedMeasurementProperties.ProcessorType != MeasurementProcessorTypes.timeRange
        ) {
            return false;
        }

        if (this.state.selectedTimeRange && measurement.duration < this.state.selectedTimeRange.from / 1000) {
            return false;
        }

        return true;
    }

    private isValidStateAndSignal(): boolean {
        return this.state != null && this.selectedSignal != null;
    }
}

export class TimeSelectionModalState {
    public selectedTimeRange: SelectableRange;
    public currentSignals: TimeDataSet[];
}

export interface AnnotatedTimeData {
    measurement: GenericMeasurement;
    dataPoints: Point2D[];
    subIdx: string;
    name: string;
    maxVal: number;
    minVal: number;

    // Index of the mic channel
    micId?: number;

    // Audio data of selected mic
    // Only used for psychoacoustic measurements as the dataPoints are the metric results, not the audio data
    // For normal Time analysis, this is undefined because the dataPoints are the audio data
    audioDataPoints?: Point2D[];
    audioMaxVal?: number;
    audioMinVal?: number;
}

export interface TimeDataSet {
    measurement: GenericMeasurement;
    selectedChannels: boolean[];
    timeDatas: AnnotatedTimeData[];
}
